import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SeoHelmet from '@/components/feature/SeoHelmet';
import { aboutInfo } from '@/mocks/comics';

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '丫丫漫画',
    url: typeof window !== 'undefined' ? `${window.location.origin}${__BASE_PATH__}` : '',
    description: aboutInfo.intro,
    foundingDate: '2020',
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutInfo.contact.email,
      telephone: aboutInfo.contact.phone,
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutInfo.contact.address,
      addressCountry: 'CN',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '关于我们 - 丫丫漫画',
    description: '了解丫丫漫画品牌故事、平台特色、用户口碑及联系方式。',
    isPartOf: {
      '@type': 'WebSite',
      name: '丫丫漫画',
    },
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutInfo.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  },
];

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  return (
    <>
      <ScrollToTop />
      <SeoHelmet
        title="关于我们 | 丫丫漫画 - 免费在线漫画阅读平台"
        description="丫丫漫画品牌介绍：国内领先的免费在线漫画阅读平台，海量韩漫、日漫、国漫资源，下拉式流畅阅读。了解我们的品牌故事与联系方式。"
        keywords="丫丫漫画,关于丫丫漫画,免费漫画平台,在线漫画,漫画品牌介绍,联系我们"
        canonicalPath="/about"
        ogType="website"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-950">
              关于{aboutInfo.brandName}
            </h1>
            <p className="mt-2 text-lg text-foreground-500">{aboutInfo.slogan}</p>
            <p className="mt-6 text-foreground-700 leading-relaxed max-w-2xl mx-auto">
              {aboutInfo.intro}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-heading text-foreground-950 text-center mb-10">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {aboutInfo.features.map((feature, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl p-6 text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-500/10 flex items-center justify-center mx-auto mb-4">
                    <i className={`text-xl text-primary-500 ${
                      ['ri-stack-line', 'ri-money-cny-circle-line', 'ri-file-list-2-line', 'ri-time-line'][index]
                    }`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-heading text-foreground-950 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {aboutInfo.reputation.map((item, index) => (
                <div key={index} className="text-center p-6">
                  <p className="text-3xl md:text-4xl font-heading text-primary-500">
                    {item.metric}
                  </p>
                  <p className="mt-2 text-sm text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-heading text-foreground-950 text-center mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-accent-500/10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-wechat-line text-lg text-accent-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-secondary-500/10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-weibo-line text-lg text-secondary-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">官方微博</p>
                <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.weibo}</p>
              </div>
              <div className="bg-background-50 rounded-xl p-5 text-center">
                <div className="w-10 h-10 rounded-lg bg-primary-500/10 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-lg text-primary-500"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">客服电话</p>
                <p className="text-sm font-medium text-foreground-900">{aboutInfo.contact.phone}</p>
              </div>
            </div>

            <div className="mt-8 bg-background-50 rounded-xl p-6 text-center">
              <p className="text-sm text-foreground-600">
                <strong className="text-foreground-900">办公地址：</strong>
                {aboutInfo.contact.address}
              </p>
              <p className="text-sm text-foreground-600 mt-1">
                <strong className="text-foreground-900">工作时间：</strong>
                {aboutInfo.contact.workingHours}
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {aboutInfo.faqs.map((faq, index) => (
                <details
                  key={index}
                  className="group bg-background-100 rounded-xl overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors">
                    <span>{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-lg transition-transform group-open:rotate-180"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}